import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Microphone, Stop, CircleNotch, X, Sparkle } from "@phosphor-icons/react";

const speakRu = (text) => {
  if (!window.speechSynthesis || !text) return;
  try {
    const u = new SpeechSynthesisUtterance(text);
    u.lang = "ru-RU";
    u.rate = 1.05;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(u);
  } catch {}
};

export const VoiceFab = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState("idle");
  const [result, setResult] = useState(null);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => () => {
    try { if (recRef.current?.state === "recording") recRef.current.stop(); } catch {}
    clearTimeout(timerRef.current);
  }, []);

  const dismiss = () => { clearTimeout(timerRef.current); setResult(null); };

  const showResult = (r) => {
    setResult(r);
    if (r.message) speakRu(r.message);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setResult(null), 9000);
  };

  const start = async () => {
    dismiss();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = window.MediaRecorder?.isTypeSupported?.("audio/webm") ? "audio/webm" : "audio/mp4";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setStatus("proc");
        try {
          const blob = new Blob(chunksRef.current, { type: mime });
          const fd = new FormData();
          fd.append("file", blob, "voice.webm");
          const { data } = await api.post("/voice/transcribe", fd);
          const { data: cmd } = await api.post("/assistant/command", { text: data.text });
          if (cmd.action === "question" && !cmd.message) {
            showResult({ transcript: data.text, question: true });
          } else {
            showResult({ transcript: data.text, message: cmd.message, image: !!cmd.image_file_id });
          }
        } catch (e) {
          showResult({ error: e?.response?.data?.detail || "Не удалось распознать — попробуйте ещё раз" });
        }
        setStatus("idle");
      };
      rec.start();
      recRef.current = rec;
      setStatus("rec");
    } catch {
      showResult({ error: "Нет доступа к микрофону — разрешите его в настройках браузера" });
    }
  };

  const onTap = () => {
    if (status === "rec") recRef.current?.stop();
    else if (status === "idle") start();
  };

  if (pathname === "/assistant") return null;

  return (
    <div className="fixed right-4 sm:right-6 bottom-[calc(env(safe-area-inset-bottom)+76px)] lg:bottom-6 z-30 flex flex-col items-end gap-2">
      {result && (
        <div className="glass-card p-3.5 w-[min(320px,calc(100vw-4rem))] shadow-2xl fade-in" data-testid="voice-fab-result">
          <div className="flex items-start gap-2">
            <div className="flex-1 min-w-0">
              {result.transcript && <p className="text-[11px] text-muted-foreground italic mb-1 line-clamp-2">«{result.transcript}»</p>}
              {result.error && <p className="text-sm text-red-400 font-medium">{result.error}</p>}
              {result.message && <p className="text-sm font-medium leading-snug">{result.message}</p>}
              {result.question && (
                <button onClick={() => { dismiss(); navigate("/assistant"); }} data-testid="voice-fab-open-assistant"
                  className="mt-1 flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline">
                  <Sparkle size={14} weight="fill" /> Это вопрос — спросить Ассистента
                </button>
              )}
              {result.image && (
                <button onClick={() => { dismiss(); navigate("/assistant"); }}
                  className="mt-1 text-xs font-semibold text-primary hover:underline">Открыть в Ассистенте →</button>
              )}
            </div>
            <button onClick={dismiss} className="p-0.5 text-muted-foreground hover:text-foreground shrink-0"><X size={14} /></button>
          </div>
        </div>
      )}
      <button onClick={onTap} data-testid="voice-fab" aria-label="Голосовая команда"
        className={`relative w-14 h-14 rounded-full flex items-center justify-center shadow-xl transition-all active:scale-95 ${
          status === "rec" ? "bg-red-500 text-white" : "bg-primary text-white hover:brightness-110"}`}>
        {status === "rec" && <span className="absolute inset-0 rounded-full bg-red-500/50 animate-ping" />}
        {status === "proc" ? <CircleNotch size={24} className="animate-spin" />
          : status === "rec" ? <Stop size={22} weight="fill" className="relative" />
          : <Microphone size={24} weight="fill" />}
      </button>
    </div>
  );
};
